import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Wrench, FileText, Users, TrendingUp, AlertTriangle, Package, CheckCircle, ShieldAlert, MessageSquare, ClipboardCheck } from "lucide-react";
import { useLocation } from "wouter";
import { useSearch } from "wouter";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import TechnicianHome from "./TechnicianHome";
import { getMaterialRequestDeepLink } from "@/lib/materialRequestNavigation";
import { getContractCertificationDeepLink } from "@/lib/certificationDeepLink";
import { canReadDashboardQueues, getDashboardExpensesQueuePath, getDashboardQueueFromSearch } from "@/finance/dashboardQueues";

const DASHBOARD_NOTE_PAGE_SIZE = 100;

export default function Home() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const activeQueue = getDashboardQueueFromSearch(search);
  const canReadQueues = canReadDashboardQueues(user?.role);
  const [notesOffset, setNotesOffset] = useState(0);
  useEffect(() => setNotesOffset(0), [activeQueue]);
  const installationsQuery = trpc.installations.list.useQuery(undefined, {
    enabled: !!user && user.role !== 'technician'
  });
  const pendingMaterialsQuery = trpc.materials.getPending.useQuery(undefined, {
    enabled: canReadQueues
  });

  // Expenses Queries (P1)
  const pendingInvoicingQuery = trpc.expenses.getPendingInvoicingCount.useQuery(undefined, {
    enabled: !!user && ['admin', 'project_manager', 'admin_manager'].includes(user.role)
  });
  const recentPendingExpensesQuery = trpc.expenses.getRecentPendingInvoicing.useQuery(undefined, {
    enabled: !!user && ['admin', 'project_manager', 'admin_manager'].includes(user.role)
  });

  // A-10: Operational Alerts
  const operationalAlertsQuery = trpc.alerts.getOperational.useQuery(undefined, {
    enabled: !!user && ['admin', 'project_manager'].includes(user.role)
  });
  const pendingNoteAlertsQuery = trpc.notes.pendingForDashboardPage.useQuery({
    offset: notesOffset,
    limit: DASHBOARD_NOTE_PAGE_SIZE,
  }, {
    enabled: canReadQueues
  });
  const certificationReviewRole = !!user && ['admin', 'project_manager', 'admin_manager'].includes(user.role);
  const certificationCapabilitiesQuery = trpc.contractCertificationsGetMyCapabilities.useQuery(undefined, {
    enabled: certificationReviewRole,
  });
  const canReadAssignedCertificationReviewQueue =
    certificationCapabilitiesQuery.data?.canRead === true &&
    certificationCapabilitiesQuery.data?.canPmReview === true;
  const pendingCertificationReviewQuery = trpc.contractCertificationsPendingPmReview.useQuery({
    userId: user?.id ?? 0,
  }, {
    enabled: certificationReviewRole && !!user?.id && canReadAssignedCertificationReviewQueue,
  });

  if (user?.role === 'technician') {
    return <TechnicianHome />;
  }

  const installations = installationsQuery.data || [];
  const pendingCount = installations.filter((i: { status: string }) => i.status === 'pending').length;
  const inProgressCount = installations.filter((i: { status: string }) => i.status === 'in_progress').length;
  const completedCount = installations.filter((i: { status: string }) => i.status === 'completed').length;
  const pendingMaterials = pendingMaterialsQuery.data || [];
  const operationalAlerts = operationalAlertsQuery.data || [];
  const pendingNotePage = pendingNoteAlertsQuery.data;
  const pendingNoteAlerts = pendingNotePage?.items || [];
  const pendingCertificationReviews = pendingCertificationReviewQuery.data ?? [];

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: "Administrador",
      project_manager: "Jefe de Proyecto",
      technician: "Técnico",
      admin_manager: "Jefe de Administración",
      sales: "Comercial",
      sales_admin: "Administrador comercial",
    };
    return labels[role] || role;
  };

  const renderPendingNoteRows = (alerts: any[]) => alerts.map((alert: any) => {
    const installationId = Number(alert.installationId);
    const target = Number.isInteger(installationId) && installationId > 0
      ? `/installations/${installationId}?tab=notes`
      : null;
    const label = alert.installationName || `instalación ${alert.installationId}`;
    return (
      <div key={`pending-note-${alert.threadId}`} className="flex flex-col gap-3 p-3 border border-l-4 border-l-blue-500 rounded bg-background shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full min-w-0 items-start gap-2 sm:flex-1">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Nota pendiente de responder</p>
            <p className="[overflow-wrap:anywhere] text-xs text-muted-foreground">{label}</p>
            <p className="mt-1 line-clamp-2 [overflow-wrap:anywhere] text-xs text-muted-foreground">{alert.noteText}</p>
          </div>
        </div>
        <Button
          className="shrink-0 self-end sm:self-auto"
          size="sm"
          variant="outline"
          disabled={!target}
          aria-label={`Ver nota pendiente en ${label}`}
          onClick={() => target && setLocation(target)}
        >
          Ver notas
        </Button>
      </div>
    );
  });

  const renderMaterialRows = (materials: any[]) => materials.map((mat: { id: number | string; materialName: string; quantity: number; installationId: number | string | null; installationAvailable?: boolean }) => {
    const target = getMaterialRequestDeepLink(mat.installationId, mat.id);
    const destinationAvailable = mat.installationAvailable !== false;
    return (
      <div key={mat.id} className="flex flex-col gap-3 p-3 border border-l-4 border-l-yellow-500 rounded bg-background shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium">Material solicitado</p>
          <p className="[overflow-wrap:anywhere] text-xs text-muted-foreground">{mat.materialName} (x{mat.quantity})</p>
        </div>
        <Button
          className="shrink-0 self-end sm:self-auto"
          size="sm"
          variant="outline"
          disabled={!target || !destinationAvailable}
          aria-label={destinationAvailable ? `Ver solicitud de ${mat.materialName}` : "Instalación no disponible"}
          onClick={() => target && destinationAvailable && setLocation(target)}
        >
          {destinationAvailable ? "Ver" : "Instalación no disponible"}
        </Button>
      </div>
    );
  });

  const renderPendingCertificationReviews = (certifications: Array<{
    customerPartnerId: number;
    contractId: number;
    contractCode: string | null;
    certificationId: number;
    certificationNumber: number;
    periodStart: string;
    periodEnd: string;
  }>) => certifications.map((certification) => {
    const target = getContractCertificationDeepLink(certification);
    const contractLabel = certification.contractCode || `Contrato #${certification.contractId}`;
    return (
      <div key={certification.certificationId} className="flex flex-col gap-3 rounded border border-l-4 border-l-violet-500 bg-background p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium">Certificación #{certification.certificationNumber} requiere revisión</p>
          <p className="[overflow-wrap:anywhere] text-xs text-muted-foreground">{contractLabel} · Período {certification.periodStart} — {certification.periodEnd}</p>
        </div>
        <Button
          className="shrink-0 self-end sm:self-auto"
          size="sm"
          variant="outline"
          disabled={!target}
          aria-label={`Revisar certificación #${certification.certificationNumber} del contrato ${contractLabel}`}
          onClick={() => target && setLocation(target)}
        >
          Revisar
        </Button>
      </div>
    );
  });

  const renderNotePagination = () => {
    if (!pendingNotePage || pendingNotePage.total <= DASHBOARD_NOTE_PAGE_SIZE) return null;
    const first = pendingNotePage.total === 0 ? 0 : notesOffset + 1;
    const last = Math.min(notesOffset + pendingNoteAlerts.length, pendingNotePage.total);
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm">
        <span className="text-muted-foreground">Notas {first}–{last} de {pendingNotePage.total}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={notesOffset === 0} onClick={() => setNotesOffset(Math.max(0, notesOffset - DASHBOARD_NOTE_PAGE_SIZE))}>Anterior</Button>
          <Button size="sm" variant="outline" disabled={notesOffset + DASHBOARD_NOTE_PAGE_SIZE >= pendingNotePage.total} onClick={() => setNotesOffset(notesOffset + DASHBOARD_NOTE_PAGE_SIZE)}>Siguiente</Button>
        </div>
      </div>
    );
  };

  const operationalAlertsRole = ["admin", "project_manager"].includes(user?.role || "");
  const queuePageError = pendingMaterialsQuery.isError || pendingNoteAlertsQuery.isError || operationalAlertsQuery.isError;
  const canConfirmQueueEmpty = canReadQueues && pendingMaterialsQuery.isSuccess && pendingNoteAlertsQuery.isSuccess &&
    (!operationalAlertsRole || operationalAlertsQuery.isSuccess);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Bienvenido, {user?.name || "Usuario"} - {getRoleLabel(user?.role || "")}
          </p>
        </div>

        {/* Metrics Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Instalaciones Totales</CardTitle>
              <Wrench className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{installations.length}</div>
              <p className="text-xs text-muted-foreground">Proyectos registrados</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Iniciadas</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inProgressCount}</div>
              <p className="text-xs text-muted-foreground">Obras activas</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
              <FileText className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingCount}</div>
              <p className="text-xs text-muted-foreground">Por iniciar</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Materiales Pendientes</CardTitle>
              <Package className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" aria-live="polite">
                {pendingMaterialsQuery.isLoading ? "…" : pendingMaterialsQuery.isError ? "!" : canReadQueues ? pendingMaterials.length : "—"}
              </div>
              <p className="text-xs text-muted-foreground">
                {pendingMaterialsQuery.isError ? "No se pudo cargar" : canReadQueues ? "Solicitudes con estado solicitado" : "No disponible para este rol"}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Recent Installations */}
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Instalaciones Recientes</CardTitle>
              <CardDescription>Últimos proyectos activos</CardDescription>
            </CardHeader>
            <CardContent>
              {installations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No hay instalaciones</div>
              ) : (
                <div className="space-y-4">
                  {installations.slice(0, 5).map((installation: { id: string; clientName: string; address: string; status: string }) => (
                    <div
                      key={installation.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => setLocation(`/installations/${installation.id}`)}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{installation.clientName}</p>
                        <p className="text-xs text-muted-foreground truncate">{installation.address}</p>
                      </div>
                      <div className={`w-2 h-2 rounded-full shrink-0 ${installation.status === 'in_progress' ? 'bg-blue-500' :
                        installation.status === 'completed' ? 'bg-green-500' :
                          installation.status === 'cancelled' ? 'bg-red-500' : 'bg-yellow-500'
                        }`} />
                    </div>
                  ))}
                  <Button variant="link" className="w-full text-xs" onClick={() => setLocation('/installations')}>
                    Ver todas
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {activeQueue ? (
            <Card className="col-span-1 min-w-0" aria-labelledby="dashboard-queue-title">
              <CardHeader>
                <CardTitle id="dashboard-queue-title" className="flex items-center gap-2">
                  {activeQueue === "materials" ? <Package className="h-5 w-5 text-yellow-600" /> : <MessageSquare className="h-5 w-5 text-blue-500" />}
                  {activeQueue === "materials" ? "Materiales solicitados" : "Notas pendientes"}
                </CardTitle>
                <CardDescription>
                  {activeQueue === "materials"
                    ? "Criterio: solicitudes cuyo estado es solicitado (requested)."
                    : `Criterio: último aporte del hilo realizado por un técnico.${pendingNotePage ? ` ${pendingNotePage.total} hilos pendientes.` : ""}`}
                </CardDescription>
                <Button variant="link" className="h-auto w-fit p-0" onClick={() => setLocation("/")}>Volver al dashboard</Button>
              </CardHeader>
              <CardContent className="min-w-0 space-y-3">
                {!canReadQueues ? (
                  <p role="alert" className="text-sm text-destructive">Esta cola no está disponible para tu rol.</p>
                ) : activeQueue === "materials" ? (
                  pendingMaterialsQuery.isLoading ? <p role="status" className="text-sm text-muted-foreground">Cargando solicitudes…</p> :
                  pendingMaterialsQuery.isError ? <p role="alert" className="text-sm text-destructive">No se pudieron cargar las solicitudes de materiales.</p> :
                  pendingMaterials.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No hay solicitudes con estado solicitado.</p> :
                  <div className="max-h-[600px] space-y-3 overflow-y-auto pr-2">{renderMaterialRows(pendingMaterials)}</div>
                ) : (
                  pendingNoteAlertsQuery.isLoading ? <p role="status" className="text-sm text-muted-foreground">Cargando notas pendientes…</p> :
                  pendingNoteAlertsQuery.isError ? <p role="alert" className="text-sm text-destructive">No se pudieron cargar las notas pendientes.</p> :
                  pendingNotePage?.total === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No hay hilos con último aporte técnico pendiente.</p> :
                  <div className="max-h-[600px] space-y-3 overflow-y-auto pr-2">
                    {renderPendingNoteRows(pendingNoteAlerts)}
                    {renderNotePagination()}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="col-span-1 min-w-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Alertas y Solicitudes
                </CardTitle>
                <CardDescription>Atención requerida</CardDescription>
              </CardHeader>
              <CardContent>
                {!canReadQueues ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">Las colas de materiales y notas no están disponibles para este rol.</p>
                ) : !canConfirmQueueEmpty && !queuePageError ? (
                  <p role="status" className="py-6 text-center text-sm text-muted-foreground">Cargando alertas pendientes…</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                    {queuePageError && <p role="alert" className="text-sm text-destructive">No se pudieron cargar todas las alertas. Los resultados visibles pueden estar incompletos.</p>}
                    {pendingNoteAlertsQuery.isError && <p role="alert" className="text-sm text-destructive">No se pudieron cargar las notas pendientes.</p>}
                    {pendingMaterialsQuery.isError && <p role="alert" className="text-sm text-destructive">No se pudieron cargar las solicitudes de materiales.</p>}
                    {operationalAlertsQuery.isError && <p role="alert" className="text-sm text-destructive">No se pudieron cargar las alertas operativas.</p>}
                    {canConfirmQueueEmpty && operationalAlerts.length === 0 && pendingMaterials.length === 0 && pendingNoteAlerts.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <CheckCircle className="h-8 w-8 mb-2 text-green-500 opacity-50" />
                        <p>Todo al día. No hay alertas ni solicitudes pendientes.</p>
                      </div>
                    ) : null}
                    {renderPendingNoteRows(pendingNoteAlerts)}
                    {pendingNotePage && pendingNotePage.total > 0 && renderNotePagination()}
                    {operationalAlerts.map((alert: any, idx: number) => (
                      <div key={`alert-${idx}`} className="flex items-center justify-between p-3 border border-l-4 border-l-orange-500 rounded bg-background shadow-sm">
                        <div className="flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-orange-500 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">{alert.message}</p>
                            <p className="text-xs text-muted-foreground">{alert.clientName}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setLocation(`/installations/${alert.installationId}`)}>Ver</Button>
                      </div>
                    ))}
                    {renderMaterialRows(pendingMaterials)}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {certificationReviewRole && (
          <Card aria-labelledby="pending-certification-review-title">
            <CardHeader className="pb-2">
              <CardTitle id="pending-certification-review-title" className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-violet-600" />
                Certificaciones pendientes de revisión
              </CardTitle>
              <CardDescription>Certificaciones asignadas a tu revisión como PM.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {certificationCapabilitiesQuery.isLoading ? (
                <p role="status" className="text-sm text-muted-foreground">Comprobando permisos de revisión…</p>
              ) : certificationCapabilitiesQuery.isError ? (
                <p role="alert" className="text-sm text-destructive">No se pudieron comprobar los permisos para cargar las certificaciones pendientes.</p>
              ) : !canReadAssignedCertificationReviewQueue ? (
                <p role="alert" className="text-sm text-destructive">Esta cola requiere permisos de lectura y revisión PM.</p>
              ) : pendingCertificationReviewQuery.isLoading ? (
                <p role="status" className="text-sm text-muted-foreground">Cargando certificaciones pendientes…</p>
              ) : pendingCertificationReviewQuery.isError ? (
                <p role="alert" className="text-sm text-destructive">No se pudieron cargar las certificaciones pendientes de revisión.</p>
              ) : pendingCertificationReviews.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No tienes certificaciones pendientes de revisión.</p>
              ) : (
                <div className="space-y-3" aria-live="polite">
                  {renderPendingCertificationReviews(pendingCertificationReviews)}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Expenses Alert (P1) */}
        {['admin', 'project_manager', 'admin_manager'].includes(user?.role || '') && (
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  Gastos pendientes: {pendingInvoicingQuery.isLoading ? "cargando…" : pendingInvoicingQuery.isError ? "no disponible" : pendingInvoicingQuery.data ?? 0}
                </CardTitle>
                <CardDescription>Gastos de técnicos pendientes de revisión administrativa.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Button variant="outline" size="sm" onClick={() => setLocation(getDashboardExpensesQueuePath())}>
                    Ver gastos pendientes
                  </Button>
                </div>
                {pendingInvoicingQuery.isLoading || recentPendingExpensesQuery.isLoading ? (
                  <p role="status" className="text-muted-foreground text-sm">Cargando gastos pendientes…</p>
                ) : pendingInvoicingQuery.isError || recentPendingExpensesQuery.isError ? (
                  <p role="alert" className="text-destructive text-sm">No se pudo cargar la cola de gastos pendientes.</p>
                ) : (!recentPendingExpensesQuery.data || recentPendingExpensesQuery.data.length === 0) ? (
                  <p className="text-muted-foreground text-sm">No hay gastos pendientes de revisión.</p>
                ) : (
                  <div className="space-y-4">
                    {recentPendingExpensesQuery.data.map((exp: any) => (
                      <div key={exp.id} className="flex justify-between items-center border-b pb-2">
                        <div>
                          <p className="font-medium text-sm">{exp.installationName}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(exp.date).toLocaleDateString()} - {exp.vendor} ({exp.amount}€)
                          </p>
                        </div>
                        <Button size="sm" variant="secondary" onClick={() => setLocation(`/installations/${exp.installationId}`)}>
                          Ver
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout >
  );
}
